import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Button, Dialog, DialogActions, LoadingBlock, Notice, PageHeader, Section } from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { can, sessionQuery } from '../api/session.ts';
import { visitQuery } from '../api/visits.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { PrescribingPanel } from '../prescribing/prescribing-panel.tsx';
import { PrescriptionPrinter } from '../prescribing/prescription-print.tsx';
import { VisitPrescriptions } from '../prescribing/visit-prescriptions.tsx';

// Prescribing for one visit (docs/plans/phase1-consultation.md §7.3). The consultation
// workspace mounts `PrescribingPanel` itself; this page is its stand-alone home until then,
// and the place the queue board's "Prescribe" opens. Only the visit's internal ID is in the
// address.

const route = getRouteApi('/_app/visits/$visitId/prescribe');

export function PrescribePage() {
  const { visitId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const visit = useQuery(visitQuery(visitId));
  const [printId, setPrintId] = useState<string | null>(null);
  const mayWrite = session !== null && can(session, 'prescriptions.write');

  if (visit.isPending) return <LoadingBlock label="Loading the visit" />;
  if (visit.isError) {
    return (
      <Notice tone="error" action={<Button onPress={() => void visit.refetch()}>Try again</Button>}>
        {visit.error instanceof ApiError
          ? visit.error.message
          : 'The visit could not load. Try again.'}
      </Notice>
    );
  }
  const patientId = visit.data.patientId;
  return (
    <>
      <PatientBannerLoader patientId={patientId} />
      <PageHeader title="Prescribe" />
      {mayWrite ? (
        visit.data.status === 'open' ? (
          <PrescribingPanel
            visitId={visitId}
            patientId={patientId}
            onSigned={(rx) => {
              // P15: the print opens by itself when a line has to be bought outside.
              if (rx.lines.some((l) => l.serviceItemId === null)) setPrintId(rx.id);
            }}
          />
        ) : (
          <Notice tone="info">
            This visit is closed, so no new prescription can be signed in it.
          </Notice>
        )
      ) : null}
      <Section title="Prescriptions in this visit">
        <VisitPrescriptions visitId={visitId} patientId={patientId} mayWrite={mayWrite} />
      </Section>
      {printId === null ? null : (
        <Dialog
          title="Prescription"
          isOpen
          onOpenChange={(open) => {
            if (!open) setPrintId(null);
          }}
        >
          <PrescriptionPrinter prescriptionId={printId} visitId={visitId} />
          <DialogActions>
            <Button
              onPress={() => {
                setPrintId(null);
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
